import React from "react";

/**
 * Accessible on/off Switch with pixel-precise knob positioning.
 *
 * Uses explicit `left` / `width` / `height` from a size table rather than
 * Tailwind translate utilities so the knob is guaranteed to stay inside the
 * track regardless of parent transform / layout context.
 */
export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  /** Track color when on. Default teal-500. */
  onColor?: string;
  /** Track color when off. Default gray-300. */
  offColor?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** Optional id forwarded to the underlying button for label association. */
  id?: string;
  className?: string;
}

const SIZES = {
  sm: { trackW: 32, trackH: 18, knob: 14, gap: 2 },
  md: { trackW: 44, trackH: 24, knob: 20, gap: 2 },
  lg: { trackW: 52, trackH: 28, knob: 22, gap: 3 },
} as const;

export const Switch: React.FC<SwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  size = "md",
  onColor = "#14B8A6",
  offColor = "#D1D5DB",
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  id,
  className,
}) => {
  const s = SIZES[size];
  const knobLeft = checked ? s.trackW - s.knob - s.gap : s.gap;
  const knobTop = (s.trackH - s.knob) / 2;

  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={[
        "relative inline-block shrink-0 rounded-full align-middle transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#14B8A6]",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        className ?? "",
      ].join(" ")}
      style={{
        width: s.trackW,
        height: s.trackH,
        backgroundColor: checked ? onColor : offColor,
      }}
    >
      <span
        aria-hidden
        className="absolute rounded-full bg-white shadow ring-1 ring-black/5 transition-[left] duration-200 ease-out"
        style={{
          width: s.knob,
          height: s.knob,
          left: knobLeft,
          top: knobTop,
        }}
      />
    </button>
  );
};

export default Switch;
